import { useMemo, PropsWithChildren } from 'react';
import { useTreeNavigationHandlers } from '../hooks/useTreeNavigationHandlers';
import useTreeNodes from '../hooks/useTreeNodes';
import TreeContext, { TreeContextValue } from './TreeContext';
import {
  TreeNode,
  NodeId,
  onLoadChildren as onLoadChildrenCallback,
  NavigableTreeNode,
} from '../types';
import { useSearchParams } from 'react-router';

interface TreeContextProviderProps {
  nodes: TreeNode[];
  onSelect: (nodeId: NodeId) => void;
  currentChapterId?: number;
  onLoadChildren?: onLoadChildrenCallback;
}

function flatten(chapters: NavigableTreeNode[]): NavigableTreeNode[] {
  return chapters.flatMap((chapter) => [
    chapter,
    ...flatten(chapter.children || []),
  ]);
}

export default function TreeContextProvider({
  nodes,
  currentChapterId,
  onSelect,
  onLoadChildren = async () => [],
  children,
}: Readonly<PropsWithChildren<TreeContextProviderProps>>) {
  const { treeNodes, updateTreeNode } = useTreeNodes(nodes, currentChapterId);
  const { handleKeyDown, handleItemToggle } = useTreeNavigationHandlers({
    treeNodes,
    updateTreeNode,
    onSelect,
    onLoadChildren,
  });
  const [searchParams] = useSearchParams();
  const flattenedNodes = useMemo(() => {
    return flatten(treeNodes);
  }, [treeNodes]);

  const currentTreeNode = useMemo(() => {
    const paramChapterId = searchParams.get('chapterId');
    if (!paramChapterId) {
      return flattenedNodes[0];
    }
    const currentChapterIdInt = parseInt(paramChapterId, 10);
    return (
      flattenedNodes.find((node) => node.nodeId === currentChapterIdInt) ?? null
    );
  }, [flattenedNodes, searchParams]);

  const currentTreeNodePath = useMemo(() => {
    if (!currentTreeNode) {
      return [];
    }

    const nodesInPath = [currentTreeNode];

    let currentNode: NavigableTreeNode | undefined = currentTreeNode;
    while (currentNode && currentNode.parentNodeId !== null) {
      const parentNode = flattenedNodes.find(
        (node) => node.nodeId === currentNode?.parentNodeId,
      );

      if (parentNode) {
        nodesInPath.push(parentNode);
      }

      currentNode = parentNode;
    }

    return nodesInPath;
  }, [currentTreeNode, flattenedNodes]);

  const contextValue: TreeContextValue = useMemo(
    () => ({
      currentTreeNode,
      currentTreeNodePath,
      handleKeyDown,
      handleItemToggle,
      onLoadChildren,
      treeNodes,
      onSelectNode: onSelect,
    }),
    [
      handleKeyDown,
      handleItemToggle,
      onLoadChildren,
      currentTreeNode,
      currentTreeNodePath,
      treeNodes,
      onSelect,
    ],
  );

  return (
    <TreeContext.Provider value={contextValue}>{children}</TreeContext.Provider>
  );
}
